import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Plus,
  RefreshCw,
  Trash2,
  Star,
  Pencil,
  X,
  Save,
  Eye,
  EyeOff,
  ExternalLink,
  Search,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  "AI Visibility",
  "Business Automation",
  "CRM Systems",
  "Websites",
  "Lead Generation",
  "Customer Experience",
  "Case Studies",
];

const slugify = (s) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const emptyArticle = () => ({
  title: "",
  slug: "",
  category: CATEGORIES[0],
  excerpt: "",
  body: "",
  featured_image: "",
  author: "Linked2Marketing",
  meta_title: "",
  meta_description: "",
  status: "draft",
  is_featured: false,
  published_at: "",
});

export default function InsightsAdmin({ token, onUnauthorized }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // article object or null
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);

  const headers = { "X-Admin-Token": token };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/insights`, { headers });
      setItems(res.data?.items || []);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
      else setError("Could not load articles.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const needle = q.toLowerCase();
    return items.filter(
      (a) =>
        a.title?.toLowerCase().includes(needle) ||
        a.category?.toLowerCase().includes(needle) ||
        a.slug?.toLowerCase().includes(needle)
    );
  }, [items, q]);

  const startCreate = () => {
    setEditing(emptyArticle());
    setCreating(true);
  };

  const startEdit = (a) => {
    setEditing({ ...a });
    setCreating(false);
  };

  const close = () => {
    setEditing(null);
    setCreating(false);
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.title.trim()) {
      setError("Title is required.");
      return;
    }
    const payload = {
      ...editing,
      slug: editing.slug?.trim() || slugify(editing.title),
    };
    setBusy(true);
    setError("");
    try {
      if (creating) {
        await axios.post(`${API}/admin/insights`, payload, { headers });
      } else {
        await axios.put(`${API}/admin/insights/${editing.id}`, payload, { headers });
      }
      await load();
      close();
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
      else if (err?.response?.status === 409)
        setError("Slug already exists. Choose a different slug.");
      else setError("Could not save article.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/insights/${id}`, { headers });
      await load();
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
      else setError("Could not delete article.");
    } finally {
      setBusy(false);
    }
  };

  const togglePublish = async (a) => {
    setBusy(true);
    try {
      const payload = {
        ...a,
        status: a.status === "published" ? "draft" : "published",
      };
      await axios.put(`${API}/admin/insights/${a.id}`, payload, { headers });
      await load();
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
      else setError("Could not update status.");
    } finally {
      setBusy(false);
    }
  };

  const setFeatured = async (a) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/insights/${a.id}/feature`,
        {},
        { headers }
      );
      await load();
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
      else setError("Could not set featured article.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-insights">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <span className="eyebrow text-gold">Insights manager</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight text-mute-100">
            Articles · {items.length}
          </h2>
          <p className="mt-3 text-[14.5px] text-mute-300">
            Manage the public Insights hub. Drafts are hidden from the live site
            until published.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-mute-300" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              data-testid="ai-search"
              className="bg-white/[0.025] border border-white/10 rounded-full pl-9 pr-3 py-2 text-[13px] text-mute-100 placeholder:text-mute-300 focus:outline-none focus:border-gold/40"
            />
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="ai-refresh"
            className="btn-secondary"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={startCreate}
            data-testid="ai-create-button"
            className="btn-primary"
          >
            <Plus className="w-4 h-4" /> New article
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-6 rounded-xl border border-red-400/30 bg-red-500/[0.07] p-4 text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="mt-8 rounded-2xl glass overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[11px] uppercase tracking-[0.18em] text-mute-300 border-b border-white/5 bg-white/[0.02]">
          <div className="col-span-5">Article</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-center">Featured</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {filtered.length === 0 && !loading && (
          <div className="px-5 py-16 text-center text-mute-300">
            No articles yet. Click <em>New article</em> to begin.
          </div>
        )}
        {filtered.map((a) => (
          <div
            key={a.id}
            data-testid={`ai-row-${a.id}`}
            className="border-b border-white/5 last:border-b-0 px-5 py-4 md:grid md:grid-cols-12 md:gap-3 md:items-center flex flex-col gap-2 hover:bg-white/[0.02] transition-colors"
          >
            <div className="md:col-span-5">
              <div className="text-[14.5px] text-mute-100 leading-tight">
                {a.title}
              </div>
              <div className="text-[12px] text-mute-300 mt-1 inline-flex items-center gap-2">
                /insights/{a.slug}
                {a.status === "published" && (
                  <a
                    href={`/insights/${a.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-teal hover:text-gold inline-flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
            <div className="md:col-span-2 text-[13px] text-mute-200">
              {a.category}
            </div>
            <div className="md:col-span-2 text-[12px]">
              <button
                type="button"
                onClick={() => togglePublish(a)}
                disabled={busy}
                data-testid={`ai-toggle-publish-${a.id}`}
                className={
                  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 border " +
                  (a.status === "published"
                    ? "border-emerald-400/30 bg-emerald-500/[0.08] text-emerald-300"
                    : "border-white/10 bg-white/[0.025] text-mute-300")
                }
              >
                {a.status === "published" ? (
                  <Eye className="w-3 h-3" />
                ) : (
                  <EyeOff className="w-3 h-3" />
                )}
                {a.status === "published" ? "Published" : "Draft"}
              </button>
            </div>
            <div className="md:col-span-1 md:text-center">
              <button
                type="button"
                onClick={() => setFeatured(a)}
                disabled={busy || a.is_featured}
                title={a.is_featured ? "Already featured" : "Set as featured"}
                data-testid={`ai-feature-${a.id}`}
                className={
                  "inline-flex items-center justify-center w-7 h-7 rounded-full transition-colors " +
                  (a.is_featured
                    ? "bg-gold/15 text-gold ring-1 ring-gold/40"
                    : "text-mute-300 hover:text-gold")
                }
              >
                <Star
                  className={`w-3.5 h-3.5 ${
                    a.is_featured ? "fill-current" : ""
                  }`}
                />
              </button>
            </div>
            <div className="md:col-span-2 md:text-right flex md:justify-end gap-2">
              <button
                type="button"
                onClick={() => startEdit(a)}
                data-testid={`ai-edit-${a.id}`}
                className="btn-secondary py-1.5 px-3 text-[12.5px]"
              >
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
              <button
                type="button"
                onClick={() => remove(a.id, a.title)}
                disabled={busy}
                data-testid={`ai-delete-${a.id}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-red-400/30 bg-red-500/[0.05] text-red-300 hover:bg-red-500/[0.10] py-1.5 px-3 text-[12.5px] transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Editor drawer */}
      {editing && (
        <Editor
          article={editing}
          setArticle={setEditing}
          creating={creating}
          busy={busy}
          onClose={close}
          onSave={save}
        />
      )}
    </div>
  );
}

function Editor({ article, setArticle, creating, busy, onClose, onSave }) {
  const set = (k, v) => setArticle({ ...article, [k]: v });

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-stretch justify-end bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="ai-editor"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl h-full overflow-y-auto bg-[#0c0d0f] border-l border-white/10 shadow-[0_30px_90px_-20px_rgba(0,0,0,0.7)]"
      >
        <div className="sticky top-0 z-10 backdrop-blur bg-[#0c0d0f]/85 border-b border-white/8 px-6 py-4 flex items-center justify-between">
          <div>
            <span className="eyebrow text-gold">
              {creating ? "New article" : "Edit article"}
            </span>
            <h3 className="mt-1 font-display text-xl text-mute-100">
              {article.title || "Untitled article"}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onSave}
              disabled={busy}
              data-testid="ai-save"
              className="btn-primary py-2 px-4 text-[13px]"
            >
              <Save className="w-4 h-4" />
              {busy ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="ai-close"
              className="btn-secondary py-2 px-4 text-[13px]"
            >
              <X className="w-4 h-4" />
              Close
            </button>
          </div>
        </div>

        <div className="px-6 py-7 space-y-6">
          <Field label="Title" required>
            <input
              autoFocus
              value={article.title}
              onChange={(e) => {
                const t = e.target.value;
                set("title", t);
                // Auto-fill slug while creating, leave it alone afterwards
                if (creating && !article._slugTouched)
                  set("slug", slugify(t));
              }}
              data-testid="ai-input-title"
              className="ai-input"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="URL slug" hint="lowercase-with-hyphens">
              <input
                value={article.slug}
                onChange={(e) => {
                  set("slug", slugify(e.target.value));
                  setArticle((prev) => ({ ...prev, _slugTouched: true }));
                }}
                data-testid="ai-input-slug"
                className="ai-input font-mono text-[13px]"
              />
            </Field>
            <Field label="Category">
              <select
                value={article.category}
                onChange={(e) => set("category", e.target.value)}
                data-testid="ai-input-category"
                className="ai-input"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field
            label="Featured image URL"
            hint="A landscape-oriented image (1600×900 ideal)."
          >
            <input
              value={article.featured_image}
              onChange={(e) => set("featured_image", e.target.value)}
              placeholder="https://…"
              data-testid="ai-input-image"
              className="ai-input"
            />
            {article.featured_image && (
              <img
                src={article.featured_image}
                alt=""
                className="mt-3 rounded-xl aspect-[16/9] object-cover w-full ring-1 ring-white/10"
              />
            )}
          </Field>

          <Field label="Excerpt" hint="1–2 sentence summary for cards & SEO.">
            <textarea
              value={article.excerpt}
              onChange={(e) => set("excerpt", e.target.value)}
              rows={3}
              data-testid="ai-input-excerpt"
              className="ai-input"
            />
          </Field>

          <Field
            label="Article body"
            hint="Plain text. Separate paragraphs with a blank line."
          >
            <textarea
              value={article.body}
              onChange={(e) => set("body", e.target.value)}
              rows={14}
              data-testid="ai-input-body"
              className="ai-input font-mono text-[13.5px] leading-[1.7]"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Author">
              <input
                value={article.author}
                onChange={(e) => set("author", e.target.value)}
                data-testid="ai-input-author"
                className="ai-input"
              />
            </Field>
            <Field label="Publish date (ISO)">
              <input
                value={article.published_at || ""}
                onChange={(e) => set("published_at", e.target.value)}
                placeholder="2026-02-27T00:00:00Z"
                data-testid="ai-input-date"
                className="ai-input font-mono text-[13px]"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Meta title" hint="SEO browser tab + search.">
              <input
                value={article.meta_title}
                onChange={(e) => set("meta_title", e.target.value)}
                data-testid="ai-input-metatitle"
                className="ai-input"
              />
            </Field>
            <Field label="Status">
              <select
                value={article.status}
                onChange={(e) => set("status", e.target.value)}
                data-testid="ai-input-status"
                className="ai-input"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </Field>
          </div>

          <Field label="Meta description" hint="155–165 chars ideal.">
            <textarea
              value={article.meta_description}
              onChange={(e) => set("meta_description", e.target.value)}
              rows={2}
              data-testid="ai-input-metadesc"
              className="ai-input"
            />
          </Field>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="text-[12px] uppercase tracking-[0.16em] text-mute-300">
        {label} {required && <span className="text-gold">*</span>}
      </span>
      <div className="mt-2">{children}</div>
      {hint && <span className="block mt-1.5 text-[11.5px] text-mute-300">{hint}</span>}
    </label>
  );
}
